<script lang="ts" setup>
import { InputSearchV2, Markdown, Skeleton } from "@/components/Ui";
import { storeToRefs } from "pinia";
import { useFaqStore } from "@/store/faq";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { getUrlSlugWithoutLocale } from "~/helpers/utilities";

definePageMeta({
  key: "faq-root",
});
const { t, locale } = useI18n();
const { faqItems, pending } = storeToRefs(useFaqStore());

useSeoMeta({
  title: t("page.faq.seo.title"),
  description: t("page.faq.seo.description"),
  ogTitle: t("page.faq.seo.title"),
  ogDescription: t("page.faq.seo.description"),
});

const localePath = computed(() => {
  return locale.value === "en" ? "" : `/${locale.value}`;
});

const search = ref<string>("");

const filterFaqItems = computed(() => {
  if (!faqItems?.value?.length) return [];
  let result;
  if (search.value) {
    const items = faqItems?.value.filter((item) => {
      const title = item?.attributes?.title?.toLowerCase();
      const description = item?.attributes?.description?.toLowerCase();
      const searchValue = search.value.toLowerCase();
      return title?.includes(searchValue) || description?.includes(searchValue);
    });
    result = items;
  } else {
    result = faqItems.value;
  }
  return result.sort((a, b) => a?.attributes?.order - b?.attributes?.order);
});
</script>

<template>
  <div :class="$style.root">
    <h1 class="text-fg-primary !text-[40px] font-semibold leading-[44px]">
      FAQ
    </h1>
    <div class="w-full">
      <InputSearchV2
        v-model="search"
        :placeholder="$t('faq.placeholder-search')"
      />
    </div>
    <ul>
      <template v-if="!pending">
        <div v-if="!filterFaqItems.length">
          {{ $t("faq.not-found") }}
        </div>
        <li
          v-for="(item, index) in filterFaqItems"
          :key="index"
        >
          <nuxt-link
            :title="item.attributes.title"
            :to="{
              path: `${localePath}/faq/${getUrlSlugWithoutLocale(
                item.attributes.slug,
              )}`,
            }"
          >
            <Markdown
              :content="item.attributes.title"
              position="nav-main"
            />

            <span class="text-[#6E7687] text-[14px] leading-[16px]"
              >{{ item?.attributes?.description }}
            </span>
          </nuxt-link>
        </li>
      </template>
      <template v-else>
        <li
          v-for="index in 3"
          :key="index"
        >
          <a>
            <Skeleton :options="{ width: 147, height: 17 }">
              <template #loader>
                <rect
                  height="17"
                  rx="4"
                  ry="4"
                  width="147"
                  x="0"
                  y="0"
                />
              </template>
            </Skeleton>
            <span>
              <Skeleton :options="{ width: 320, height: 35 }">
                <template #loader>
                  <rect
                    height="35"
                    rx="8"
                    ry="8"
                    width="320"
                    x="0"
                    y="0"
                  />
                </template>
              </Skeleton>
            </span>
          </a>
        </li>
      </template>
    </ul>
  </div>
</template>

<style lang="scss" module>
.root {
  @apply flex flex-col gap-[40px] pt-10 md:pt-0;
  & h1 {
    @apply text-neutral-800 text-[50px] xxs:text-[54px] md:text-[120px] leading-none font-extrabold;
  }

  & ul {
    @apply flex flex-col gap-4;
    & li {
      @apply rounded-[16px] overflow-hidden transition-all;
      @apply cursor-pointer bg-neutral-100 hover:bg-neutral-200 hover:bg-opacity-50;

      & a {
        @apply flex flex-col gap-2 px-4 py-5;

        & span p {
          @apply font-semibold text-[24px] leading-snug text-neutral-900 inline-flex xxs:gap-2 items-center flex-wrap;
        }

        & p {
          @apply text-sm text-neutral-500 leading-snug;
        }
      }
    }
  }

  &__desc {
    @apply flex flex-col gap-3;

    & p {
      &:first-child {
        @apply text-neutral-800 text-[20px] md:text-[40px] leading-tight;
      }

      &:last-child {
        @apply text-neutral-500 text-[15px] md:text-2xl leading-snug;
      }
    }
  }
}
</style>
